// ReorderStopsModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { GripVertical } from "lucide-react";
import httpClient from "../../../../../../api/httpClient";
import Cookies from "js-cookie";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";

/** Draggable row for non-pickup stops */
const SortableStopItem = ({ stop, index, id }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex justify-between items-center py-fluid-1.5 px-fluid-2 bg-white rounded-lg border border-gray-200 shadow-sm mb-fluid-1 text-fluid-sm text-gray-800 cursor-grab hover:shadow-md transition"
    >
      <span className="truncate">
        {index}. {stop.stopName}, {stop.stopType} – {stop.city}, {stop.state}
      </span>
      <GripVertical className="text-gray-400" size={18} />
    </div>
  );
};

/**
 * Props:
 *  - onClose(): void
 *  - onConfirm(updatedStops: Stop[]): void
 *  - shipmentNumber: string
 *  - stops: Array<{ id, stopName, stopType, city, state, stopSequence }>
 *
 * Layout intentionally kept the same as before:
 * Header → Description → "Fixed Pickup Stop" → "Reorder Remaining Stops" → Buttons
 */
const ReorderStopsModal = ({ onClose, onConfirm, shipmentNumber, stops }) => {
  // Sort by stopNumber (lowest→highest)
  const sortedStops = useMemo(
    () =>
      [...(stops ?? [])].sort((a, b) => {
        const aNum = parseInt(a.stopNumber) || 0;
        const bNum = parseInt(b.stopNumber) || 0;
        return aNum - bNum;
      }),
    [stops]
  );

  // Pin the pickup at the top:
  // prefer first PICKUP/ORIGIN by stopNumber; if none, pin the first stop
  const pickupStop = useMemo(() => {
    if (!sortedStops.length) return null;
    const candidates = sortedStops.filter((s) =>
      ["PICKUP", "ORIGIN"].includes(String(s.stopType || "").toUpperCase())
    );
    if (candidates.length === 0) return sortedStops[0];
    return candidates[0]; // First PICKUP/ORIGIN in sorted order
  }, [sortedStops]);

  // Draggable list = everything except the pinned pickup (maintain sort order)
  const reorderableStops = useMemo(() => {
    if (!pickupStop) return [];
    const filtered = sortedStops.filter((s) => s.id !== pickupStop.id);
    // Sort by stopNumber to maintain current order and track changes correctly
    return filtered.sort((a, b) => {
      const aNum = parseInt(a.stopNumber) || 0;
      const bNum = parseInt(b.stopNumber) || 0;
      return aNum - bNum;
    });
  }, [sortedStops, pickupStop]);

  // Map id -> stop for quick lookup
  const idToStopMap = useMemo(() => {
    const m = {};
    sortedStops.forEach((s) => (m[s.id] = s));
    return m;
  }, [sortedStops]);

  // Original order of reorderables (ids)
  const originalOrderIds = useMemo(
    () => reorderableStops.map((s) => s.id),
    [reorderableStops]
  );

  // Current draggable order (ids) - only initialize once
  const [items, setItems] = useState(() => originalOrderIds);
  useEffect(() => {
    // Only reset if the pickup stop changed (new modal opened with different shipment)
    // Don't reset during re-renders of the same modal
    if (items.length === 0) {
      setItems(originalOrderIds);
    }
  }, []);

  // Snapshot original stopNumbers (id -> stopNumber)
  const originalStopNumbers = useMemo(() => {
    const num = {};
    sortedStops.forEach((s) => (num[s.id] = parseInt(s.stopNumber) || 0));
    return num;
  }, [sortedStops]);

  // No-op detection (hasChanges flag)
  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    const sameLength = items.length === originalOrderIds.length;
    const shallowSame = sameLength && items.every((id, i) => id === originalOrderIds[i]);
    setHasChanges(!shallowSame);
  }, [items, originalOrderIds]);

  // DnD setup
  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = (event) => {
    const { active, over } = event || {};
    if (!over || !active) return;
    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        setItems((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleConfirmReorder = async () => {
    if (!pickupStop) {
      notify.error({ title: "Error", message: "No pickup stop found." });
      return;
    }

    // Final compact order always starts with pickup
    const finalOrderedStops = [pickupStop, ...items.map((id) => idToStopMap[id]).filter(Boolean)];

    // Only non-pickup updates, only if stopNumber actually changed
    const sequenceUpdates = finalOrderedStops
      .map((stop, idx) => {
        const newStopNum = idx + 1; // 1-based
        const origStopNum = originalStopNumbers[stop.id];
        if (pickupStop && stop.id === pickupStop.id) return null; // never update pickup
        return origStopNum !== newStopNum
          ? { originalStopSequence: origStopNum, newStopSequence: newStopNum }
          : null;
      })
      .filter(Boolean);

    // No-op → show toast and bail (no API)
    if (!hasChanges || sequenceUpdates.length === 0) {
      notify.info({ title: "Info", message: "Stop ordering is the same as before. No changes to apply." });
      return;
    }

    setIsLoading(true);
    try {
      const token = Cookies.get("authToken");
      const officeCode = Cookies.get("officeCode");

      const response = await httpClient.post(
        `/api/stops/reorder-stops-on-shipment?shipmentNumber=${encodeURIComponent(
          shipmentNumber
        )}`,
        sequenceUpdates,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": officeCode,
          },
        }
      );

      // Status toasts (keep your existing strings)
      const msg = response?.data;
      if (msg === "REORDERED_STOPS") {
        notify.success({ title: "Success", message: "Stops reordered successfully!" });
      } else if (msg === "REORDERED_STOPS_WITH_CARRIER") {
        notify.success({ title: "Success", message: "Stops reordered successfully. Carrier retained with updated charges." });
      } else if (msg === "REORDERED_STOPS_NO_CARRIER") {
        notify.warning({ title: "Warning", message: "Stops reordered successfully. Moving shipment to unassigned due to carrier discrepancy." });
      } else if (msg === "REORDERED_STOPS_NO_LANES") {
        notify.warning({ title: "Warning", message: "Stops reordered successfully. Shipment moved to unassigned as there was no carrier and no charges." });
      } else {
        notify.success({ title: "Success", message: "Stops reordered successfully!" });
      }

      // Normalize locally to 1…N (pickup at 1), so parent view stays consistent
      const normalizedStops = finalOrderedStops.map((s, i) => ({
        ...s,
        stopNumber: String(i + 1),
      }));

      onConfirm(normalizedStops);
      onClose();

      // Refresh the page after a short delay to show updated stops
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Failed to reorder stops:", error);
      notify.error({
        title: "Error",
        message: error?.response?.data?.message ?? "Failed to reorder stops. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  // === Modal layout with proper styling ===
  return (
    <motion.div
      className="fixed inset-0 z-[1000] bg-black bg-opacity-40 flex items-center justify-center px-fluid-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-fluid-3 space-y-fluid-3"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* Header */}
        <div className="flex flex-col items-center">
          <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white">
            <GripVertical size={28} />
          </div>
          <h2 className="text-fluid-xl font-semibold mt-fluid-1.5 text-[#111827]">
            Reorder Shipment Stops
          </h2>
        </div>

        {/* Description */}
        <p className="text-fluid-base text-gray-700 text-center">
          Drag and reorder the stops for shipment{" "}
          <span className="font-medium text-[#1e2a74]">{shipmentNumber}</span>
        </p>

        {/* Fixed Pickup Stop */}
        {pickupStop && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-fluid-2.5">
            <p className="text-fluid-sm font-semibold text-[#1e2a74] mb-fluid-1">
              Fixed Pickup Stop
            </p>
            <p className="text-fluid-sm text-gray-800">
              1. {pickupStop.stopName}, {pickupStop.stopType} – {pickupStop.city},{" "}
              {pickupStop.state}
            </p>
          </div>
        )}

        {/* Draggable stops (non-pickup) */}
        <div>
          <p className="text-fluid-sm text-gray-600 mb-fluid-1 font-medium">
            Reorder Remaining Stops
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              <div className="bg-gray-50 rounded-lg border px-fluid-2 py-fluid-1.5 max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
                {items.map((id, i) => (
                  <SortableStopItem
                    key={id}
                    id={id}
                    index={i + 2}
                    stop={idToStopMap[id]}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmReorder}
            disabled={isLoading}
            className="px-fluid-button-x py-fluid-button-y bg-[#1e2a74] text-white hover:bg-[#18225c] text-fluid-sm rounded font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Reordering..." : "Confirm Reorder"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReorderStopsModal;
